/**
 * Resumen del modal «Total Células» del Dashboard.
 * Usa cells + members ya cargados (member.cell_id = pertenencia a célula).
 */
import { getCellMongoId } from "@/lib/cellIds";
import {
  CELL_MEETING_DAY_OPTIONS,
  cellMeetingDayLabel,
  cellMeetingModalityLabel,
  cellMeetingTimeLabel,
} from "@/lib/cellFormLabels";
import { MEMBER_SEX_F, MEMBER_SEX_M } from "@/lib/memberSex";

const DAY_ORDER = CELL_MEETING_DAY_OPTIONS.map((o) => o.value);
export const CELLS_OVERVIEW_UNASSIGNED_DAY_KEY = "__unassigned__";

const NETWORK_LABELS = {
  discipulado_pastores: "Discipulado Pastores",
  adultos: "Adultos",
  jovenes: "Jóvenes",
  ninos: "Niños",
};

export function formatCellNetworkLabel(networkType) {
  const key = String(networkType || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  if (!key) return "Sin clasificar";
  return NETWORK_LABELS[key] || key;
}

function memberId(m) {
  return String(m?.id || m?._id || "").trim();
}

function meetingDayKey(cell) {
  const raw = String(cell?.meeting_day || "")
    .trim()
    .toLowerCase()
    .replace("é", "e")
    .replace("á", "a")
    .replace("í", "i")
    .replace("ó", "o")
    .replace("ú", "u");
  if (!raw) return CELLS_OVERVIEW_UNASSIGNED_DAY_KEY;
  if (DAY_ORDER.includes(raw)) return raw;
  return CELLS_OVERVIEW_UNASSIGNED_DAY_KEY;
}

function timeSortValue(time) {
  const s = String(time || "").trim();
  if (!s) return Number.POSITIVE_INFINITY;
  const parts = s.split(":");
  if (parts.length < 2) return Number.POSITIVE_INFINITY;
  const hh = Number(parts[0]);
  const mm = Number(parts[1]);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return Number.POSITIVE_INFINITY;
  return hh * 60 + mm;
}

function roundPct(n, total) {
  if (!total) return 0;
  return Math.round((n / total) * 1000) / 10;
}

/**
 * @param {{ cells: Array, members: Array, getMemberName: (id: string) => string }} args
 */
export function buildCellsOverview({ cells, members, getMemberName }) {
  const cellList = Array.isArray(cells) ? cells : [];
  const memberList = Array.isArray(members) ? members : [];

  const cellById = new Map();
  for (const c of cellList) {
    const id = getCellMongoId(c);
    if (id) cellById.set(id, c);
  }

  // Personas únicas con célula existente (home cell_id); evita doble conteo por id
  const seen = new Set();
  const membersWithCell = [];
  for (const m of memberList) {
    const cid = String(m?.cell_id || "").trim();
    if (!cid || !cellById.has(cid)) continue;
    const mid = memberId(m);
    if (!mid || seen.has(mid)) continue;
    seen.add(mid);
    membersWithCell.push(m);
  }

  const countByCellId = new Map();
  for (const m of membersWithCell) {
    const cid = String(m.cell_id).trim();
    countByCellId.set(cid, (countByCellId.get(cid) || 0) + 1);
  }

  const rows = cellList.map((cell) => {
    const id = getCellMongoId(cell);
    const dayKey = meetingDayKey(cell);
    return {
      id,
      name: cell.name || "Sin nombre",
      network_type: cell.network_type || "",
      networkLabel: formatCellNetworkLabel(cell.network_type),
      leaderName: getMemberName ? getMemberName(cell.leader_id) : "Sin asignar",
      address: cell.address || "Sin dirección",
      location: cell.location || "Sin zona",
      meeting_modality: cell.meeting_modality || "",
      modalityLabel: cellMeetingModalityLabel(cell.meeting_modality, { short: true }),
      meeting_day: cell.meeting_day || "",
      dayKey,
      dayLabel:
        dayKey === CELLS_OVERVIEW_UNASSIGNED_DAY_KEY
          ? "Día pendiente de asignar"
          : cellMeetingDayLabel(cell.meeting_day) || "Día pendiente de asignar",
      meeting_time: cell.meeting_time || "",
      timeLabel: cellMeetingTimeLabel(cell.meeting_time),
      membersCount: id ? countByCellId.get(id) || 0 : 0,
    };
  });

  const dayGroups = [];
  for (const day of DAY_ORDER) {
    const groupRows = rows
      .filter((r) => r.dayKey === day)
      .sort((a, b) => {
        const ta = timeSortValue(a.meeting_time);
        const tb = timeSortValue(b.meeting_time);
        if (ta !== tb) return ta - tb;
        return String(a.name).localeCompare(String(b.name), "es");
      });
    if (groupRows.length) {
      dayGroups.push({
        key: day,
        label: cellMeetingDayLabel(day) || day,
        rows: groupRows,
      });
    }
  }
  const unassigned = rows
    .filter((r) => r.dayKey === CELLS_OVERVIEW_UNASSIGNED_DAY_KEY)
    .sort((a, b) => {
      const ta = timeSortValue(a.meeting_time);
      const tb = timeSortValue(b.meeting_time);
      if (ta !== tb) return ta - tb;
      return String(a.name).localeCompare(String(b.name), "es");
    });
  if (unassigned.length) {
    dayGroups.push({
      key: CELLS_OVERVIEW_UNASSIGNED_DAY_KEY,
      label: "Día pendiente de asignar",
      rows: unassigned,
    });
  }

  const totalCells = cellList.length;
  const totalPeople = membersWithCell.length;
  const avgMembers = totalCells ? Math.round((totalPeople / totalCells) * 10) / 10 : 0;

  // Desglose por red de la célula a la que pertenece cada persona
  const networkCounts = new Map();
  for (const m of membersWithCell) {
    const cid = String(m.cell_id).trim();
    const cell = cellById.get(cid);
    const netKey = String(cell?.network_type || "")
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, "_") || "__sin_clasificar__";
    networkCounts.set(netKey, (networkCounts.get(netKey) || 0) + 1);
  }

  const networkBreakdown = [...networkCounts.entries()]
    .map(([key, count]) => ({
      key,
      label: key === "__sin_clasificar__" ? "Sin clasificar" : formatCellNetworkLabel(key),
      count,
      percent: roundPct(count, totalPeople),
    }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, "es"));

  // Distribución por sexo (campo member.sex: M | F)
  let menCount = 0;
  let womenCount = 0;
  let sexUnspecifiedCount = 0;
  for (const m of membersWithCell) {
    const s = String(m?.sex ?? "").trim().toUpperCase();
    if (s === MEMBER_SEX_M) menCount += 1;
    else if (s === MEMBER_SEX_F) womenCount += 1;
    else sexUnspecifiedCount += 1;
  }

  const sexBreakdown = [
    {
      key: MEMBER_SEX_M,
      label: "Hombres",
      count: menCount,
      percent: roundPct(menCount, totalPeople),
    },
    {
      key: MEMBER_SEX_F,
      label: "Mujeres",
      count: womenCount,
      percent: roundPct(womenCount, totalPeople),
    },
  ];
  if (sexUnspecifiedCount > 0) {
    sexBreakdown.push({
      key: "unspecified",
      label: "Sin especificar",
      count: sexUnspecifiedCount,
      percent: roundPct(sexUnspecifiedCount, totalPeople),
    });
  }

  return {
    totalCells,
    totalPeople,
    avgMembers,
    networkBreakdown,
    sexBreakdown,
    dayGroups,
  };
}
